import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Chip, 
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, 
  Snackbar, Alert, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, FormControl, InputLabel, Select, 
  MenuItem, IconButton, Tooltip, CircularProgress, Divider,
  Accordion, AccordionSummary, AccordionDetails, List, ListItem,
  ListItemText, Collapse, useTheme, useMediaQuery
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  AssignmentOutlined as AssignmentIcon,
  CheckCircleOutline as CheckCircleIcon,
  CancelOutlined as CancelIcon,
  VisibilityOutlined as VisibilityIcon,
  FilterListOutlined as FilterListIcon,
  RefreshOutlined as RefreshIcon,
  AddOutlined as AddIcon,
  WarningAmberOutlined as WarningIcon,
  LinkOutlined as LinkIcon,
  ContentCopyOutlined as ContentCopyIcon,
  PictureAsPdf as PdfIcon,
  DownloadOutlined as DownloadIcon,
  EmailOutlined as EmailIcon,
  PhoneOutlined as PhoneIcon,
  NoteOutlined as NoteIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import RentalApplicationForm from './RentalApplicationForm';
import ApplicantSinField from './ApplicantSinField';
import api from '../api';
import { publicApplicationUrl } from '../utils/showings';

const RentalApplicationManagement = () => {
  const SCREENING_ACTIVITY_PREVIEW_LIMIT = 5;
  const SCREENING_ACTIVITY_DIALOG_LIMIT = 100;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [applications, setApplications] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProperty, setFilterProperty] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedPropertyForLink, setSelectedPropertyForLink] = useState('');
  const [selectedUnitForLink, setSelectedUnitForLink] = useState('');
  const [availableUnitsForLink, setAvailableUnitsForLink] = useState([]);
  const [landlordSlug, setLandlordSlug] = useState('');
  const [signatureData, setSignatureData] = useState(new Map()); // Map of applicationId -> signature info
  const [loadingSignatures, setLoadingSignatures] = useState(false);
  const [filterSignature, setFilterSignature] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [quickNote, setQuickNote] = useState(''); // Quick note for call scripts
  const [showCallScript, setShowCallScript] = useState(null); // 'landlord' | 'employment' | 'personal' | null
  const [expandedNotes, setExpandedNotes] = useState(false);
  const [screeningOrders, setScreeningOrders] = useState([]);
  const [loadingScreeningOrders, setLoadingScreeningOrders] = useState(false);
  const [screeningOrderEventsById, setScreeningOrderEventsById] = useState({});
  const [screeningOrderEventsPageById, setScreeningOrderEventsPageById] = useState({});
  const [loadingScreeningOrderEventsById, setLoadingScreeningOrderEventsById] = useState({});
  const [loadingMoreScreeningOrderEventsById, setLoadingMoreScreeningOrderEventsById] = useState({});
  const [expandedScreeningOrderEventsById, setExpandedScreeningOrderEventsById] = useState({});
  const [screeningActivityDialogOpen, setScreeningActivityDialogOpen] = useState(false);
  const [screeningActivityDialogOrderId, setScreeningActivityDialogOrderId] = useState(null);
  const [screeningSetup, setScreeningSetup] = useState(null); // from /api/settings (showProviderComparisonLink, etc.)
  const [requestScreeningDialogOpen, setRequestScreeningDialogOpen] = useState(false);
  const [screeningConfig, setScreeningConfig] = useState({ countries: ['CA', 'US'], packages: [] });
  const [simpleScreeningCountry, setSimpleScreeningCountry] = useState('CA');
  const [simpleScreeningPackageId, setSimpleScreeningPackageId] = useState('STANDARD');
  const [simpleScreeningPaidBy, setSimpleScreeningPaidBy] = useState('applicant');
  const [simpleScreeningCreating, setSimpleScreeningCreating] = useState(false);
  const [lastInviteUrl, setLastInviteUrl] = useState('');
  const [externalCompletionDialogOpen, setExternalCompletionDialogOpen] = useState(false);
  const [externalCompletionOrder, setExternalCompletionOrder] = useState(null);
  const [externalCompletionNotes, setExternalCompletionNotes] = useState('');
  const [externalCompletionProvider, setExternalCompletionProvider] = useState('');
  const [externalCompletionReportUrl, setExternalCompletionReportUrl] = useState('');
  const [externalCompletionSubmitting, setExternalCompletionSubmitting] = useState(false);

  useEffect(() => {
    fetchProperties();
    fetchApplications();
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/api/settings');
        setScreeningSetup(res.data?.screeningSetup ?? null);
      } catch (_) {
        setScreeningSetup(null);
      }
    };
    fetchSettings();
  }, []);

  // Force button styles on mobile using CSS-only approach
  useEffect(() => {
    const styleId = 'rental-app-button-override';
    let styleElement = document.getElementById(styleId);
    
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      styleElement.textContent = `
        @media (max-width: 600px) {
          .rental-app-buttons-wrapper {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            box-sizing: border-box !important;
            overflow: hidden !important;
          }
          .rental-app-buttons-wrapper .MuiButton-root,
          .rental-app-button-mobile {
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
            flex-shrink: 0 !important;
            flex-grow: 0 !important;
            box-sizing: border-box !important;
          }
        }
      `;
      document.head.appendChild(styleElement);
    }
    
    // Force styles function
    const forceStyles = () => {
      if (window.innerWidth <= 600) {
        const wrapper = document.querySelector('.rental-app-buttons-wrapper');
        const buttons = document.querySelectorAll('.rental-app-buttons-wrapper .MuiButton-root, .rental-app-button-mobile');
        
        if (wrapper) {
          wrapper.style.setProperty('width', '100%', 'important');
          wrapper.style.setProperty('max-width', '100%', 'important');
          wrapper.style.setProperty('min-width', '0', 'important');
          wrapper.style.setProperty('box-sizing', 'border-box', 'important');
        }
        
        buttons.forEach(button => {
          if (button instanceof HTMLElement) {
            button.style.setProperty('width', '100%', 'important');
            button.style.setProperty('min-width', '0', 'important');
            button.style.setProperty('max-width', '100%', 'important');
            button.style.setProperty('flex-shrink', '0', 'important');
            button.style.setProperty('flex-grow', '0', 'important');
            button.style.setProperty('box-sizing', 'border-box', 'important');
          }
        });
      }
    };
    
    // Run immediately
    forceStyles();
    
    // Use requestAnimationFrame for next frame
    const rafId = requestAnimationFrame(() => {
      forceStyles();
    });
    
    // Set up timeouts
    const timeouts = [50, 100, 200, 500, 1000, 2000, 3000].map(delay => 
      setTimeout(forceStyles, delay)
    );
    
    // Set up MutationObserver
    let observer = null;
    const checkAndObserve = () => {
      const wrapper = document.querySelector('.rental-app-buttons-wrapper');
      if (wrapper && !observer) {
        observer = new MutationObserver(() => {
          forceStyles();
        });
        
        observer.observe(wrapper, {
          attributes: true,
          attributeFilter: ['style', 'class'],
          childList: true,
          subtree: true
        });
      }
    };
    
    // Check for wrapper after a delay
    setTimeout(checkAndObserve, 100);
    
    // Also run on window resize
    window.addEventListener('resize', forceStyles);
    
    // Continuous enforcement - run every 100ms
    const intervalId = setInterval(forceStyles, 100);
    
    return () => {
      cancelAnimationFrame(rafId);
      timeouts.forEach(clearTimeout);
      clearInterval(intervalId);
      window.removeEventListener('resize', forceStyles);
      if (observer) {
        observer.disconnect();
      }
    };
  }, []); // Empty deps - run once on mount

  // Debug: Log when component mounts with signature features
  useEffect(() => {
    console.log('RentalApplicationManagement: Signature UI features loaded', {
      hasSignatureData: signatureData.size > 0,
      filterSignature,
      applicationsCount: applications.length
    });
  }, [signatureData, filterSignature, applications.length]);

  const fetchProperties = async () => {
    try {
      const response = await api.get('/api/properties');
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  const handlePropertyChangeForLink = async (propertyId) => {
    setSelectedPropertyForLink(propertyId);
    setSelectedUnitForLink('');
    
    if (!propertyId) {
      setAvailableUnitsForLink([]);
      return;
    }

    try {
      const response = await api.get(`/api/properties/${propertyId}/units`);
      const units = (response.data || []).map(unit => ({
        id: unit.id || unit.unitId,
        name: unit.name || unit.unitNumber || `Unit ${unit.id}`,
        propertyId: propertyId,
        fullLabel: `${properties.find(p => p.id === propertyId)?.name || 'Property'} - ${unit.name || unit.unitNumber || 'Unit'}`
      }));
      setAvailableUnitsForLink(units);
    } catch (error) {
      console.error('Error fetching units:', error);
      setAvailableUnitsForLink([]);
    }
  };

  // The application link reuses the landlord's booking slug so only their properties are listed.
  const fetchLandlordSlug = async () => {
    if (landlordSlug) return;
    try {
      const response = await api.get('/api/showings/booking-link');
      if (response.data?.slug) setLandlordSlug(response.data.slug);
    } catch (error) {
      console.error('❌ Error loading application link:', error);
      setSnackbar({ open: true, message: 'Could not load your application link. Please try again.', severity: 'error' });
    }
  };

  const generatePublicLink = () =>
    publicApplicationUrl({
      slug: landlordSlug,
      propertyId: selectedPropertyForLink,
      unitId: selectedUnitForLink
    });

  const copyLinkToClipboard = () => {
    const link = generatePublicLink();
    if (!link) return;
    navigator.clipboard.writeText(link).then(() => {
      setSnackbar({
        open: true,
        message: 'Link copied to clipboard!',
        severity: 'success'
      });
    }).catch(() => {
      setSnackbar({
        open: true,
        message: 'Failed to copy link',
        severity: 'error'
      });
    });
  };

  const fetchSignatureRequest = async (signatureRequestId) => {
    try {
      const response = await api.get(`/api/e-signature/status/${signatureRequestId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching signature request:', error);
      return null;
    }
  };

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }
      if (filterProperty !== 'all') {
        params.propertyId = filterProperty;
      }
      
      const response = await api.get('/api/applications/all', { params });
      setApplications(response.data);
      
      // Fetch signature data for applications with credit consent
      setLoadingSignatures(true);
      const signaturePromises = response.data
        .filter(app => app.creditConsentSignatureRequestId)
        .map(async (app) => {
          const sigData = await fetchSignatureRequest(app.creditConsentSignatureRequestId);
          return { applicationId: app.id, signatureData: sigData };
        });
      
      const signatureResults = await Promise.all(signaturePromises);
      const newSignatureMap = new Map();
      signatureResults.forEach(({ applicationId, signatureData }) => {
        if (signatureData) {
          newSignatureMap.set(applicationId, signatureData);
        }
      });
      setSignatureData(newSignatureMap);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load applications',
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setLoadingSignatures(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [filterStatus, filterProperty, filterSignature]);

  const getSignatureStatus = (application) => {
    if (application.additionalInfo?.creditReportConsent !== true) {
      return 'not_required';
    }
    
    if (!application.creditConsentSignatureRequestId) {
      return 'not_sent';
    }
    
    const sigData = signatureData.get(application.id);
    if (!sigData) {
      return loadingSignatures ? 'loading' : 'unknown';
    }
    
    return sigData.status === 'completed' ? 'signed' : 'pending';
  };

  const getSignatureStatusColor = (status) => {
    switch (status) {
      case 'signed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'not_required':
        return 'default';
      case 'not_sent':
        return 'error';
      default:
        return 'default';
    }
  };

  const getSignatureStatusLabel = (status) => {
    switch (status) {
      case 'signed':
        return 'Signed';
      case 'pending':
        return 'Pending';
      case 'not_required':
        return 'Not Required';
      case 'not_sent':
        return 'Not Sent';
      case 'loading':
        return 'Loading...';
      default:
        return 'Unknown';
    }
  };

  const handleViewSignedPDF = async (signatureRequestId) => {
    try {
      const response = await api.get(`/api/e-signature/pdf/${signatureRequestId}/signed`);
      if (response.data.pdfUrl) {
        window.open(response.data.pdfUrl, '_blank');
      } else {
        setSnackbar({
          open: true,
          message: 'Signed PDF not available yet',
          severity: 'warning'
        });
      }
    } catch (error) {
      console.error('Error fetching signed PDF:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load signed PDF',
        severity: 'error'
      });
    }
  };

  const handleDownloadSignedPDF = async (signatureRequestId) => {
    try {
      const response = await api.get(`/api/e-signature/pdf/${signatureRequestId}/signed`);
      if (response.data.pdfUrl) {
        // Create a temporary link to download the PDF
        const link = document.createElement('a');
        link.href = response.data.pdfUrl;
        link.download = `credit-consent-${signatureRequestId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setSnackbar({
          open: true,
          message: 'Signed PDF not available yet',
          severity: 'warning'
        });
      }
    } catch (error) {
      console.error('Error downloading signed PDF:', error);
      setSnackbar({
        open: true,
        message: 'Failed to download signed PDF',
        severity: 'error'
      });
    }
  };

  const handleResendSignatureEmail = async (application) => {
    try {
      // This would require a backend endpoint to resend the email
      // For now, we'll show a message that this feature needs to be implemented
      setSnackbar({
        open: true,
        message: 'Resend email feature - to be implemented',
        severity: 'info'
      });
    } catch (error) {
      console.error('Error resending signature email:', error);
      setSnackbar({
        open: true,
        message: 'Failed to resend email',
        severity: 'error'
      });
    }
  };

  const handleViewDetails = (application) => {
    setSelectedApplication(application);
    setDetailDialogOpen(true);
    setQuickNote(''); // Clear quick note when opening new application
    setShowCallScript(null); // Reset call script view
    setScreeningOrders([]);
    setScreeningOrderEventsById({});
    setLoadingScreeningOrderEventsById({});
    setExpandedScreeningOrderEventsById({});
    setScreeningActivityDialogOpen(false);
    setScreeningActivityDialogOrderId(null);
    // Load notes for this application
    fetchNotes(application.id, application.propertyId, application.unitId);
  };

  // Fetch tenant screening orders when application detail is open
  useEffect(() => {
    if (!detailDialogOpen || !selectedApplication?.id) return;
    let cancelled = false;
    setLoadingScreeningOrders(true);
    api.get('/api/billing/screeningOrders', { params: { applicationId: selectedApplication.id } })
      .then((res) => { if (!cancelled) setScreeningOrders(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setScreeningOrders([]); })
      .finally(() => { if (!cancelled) setLoadingScreeningOrders(false); });
    return () => { cancelled = true; };
  }, [detailDialogOpen, selectedApplication?.id]);

  const openRequestScreeningDialog = async () => {
    setRequestScreeningDialogOpen(true);
    setLastInviteUrl('');
    try {
      const res = await api.get('/api/billing/comparison-config');
      setScreeningConfig(res.data || { countries: ['CA', 'US'], packages: [] });
    } catch (_) {
      setScreeningConfig({ countries: ['CA', 'US'], packages: [] });
    }
  };

  const handleCreateSimpleScreeningOrder = async () => {
    if (!selectedApplication?.id) return;
    const pkg = screeningConfig.packages.find(p => p.id === simpleScreeningPackageId);
    const skuFromConfig = pkg?.skuByCountry?.[simpleScreeningCountry];
    const fallbackSkus = {
      CA: { BASIC: 'SCREEN_BASIC_CA', STANDARD: 'SCREEN_STANDARD_CA', PREMIUM: 'SCREEN_PREMIUM_CA' },
      US: { BASIC: 'SCREEN_BASIC_US', STANDARD: 'SCREEN_STANDARD_US', PREMIUM: 'SCREEN_PREMIUM_US' }
    };
    const sku = skuFromConfig || fallbackSkus[simpleScreeningCountry]?.[simpleScreeningPackageId] || (simpleScreeningCountry === 'US' ? 'SCREEN_STANDARD_US' : 'SCREEN_STANDARD_CA');
    try {
      setSimpleScreeningCreating(true);
      setLastInviteUrl('');
      const res = await api.post('/api/billing/screeningOrders', {
        applicationId: selectedApplication.id,
        sku,
        paidBy: simpleScreeningPaidBy === 'landlord' ? 'landlord' : 'applicant'
      });
      const url = res.data?.inviteUrl || '';
      setLastInviteUrl(url);
      if (url && selectedApplication?.id) {
        setSnackbar({ open: true, message: 'Screening order created. Share the invite link with the applicant.', severity: 'success' });
        const listRes = await api.get('/api/billing/screeningOrders', { params: { applicationId: selectedApplication.id } });
        setScreeningOrders(Array.isArray(listRes.data) ? listRes.data : []);
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to create screening order', severity: 'error' });
    } finally {
      setSimpleScreeningCreating(false);
    }
  };

  const openExternalCompletionDialog = (order) => {
    setExternalCompletionOrder(order);
    setExternalCompletionNotes('');
    setExternalCompletionProvider('');
    setExternalCompletionReportUrl('');
    setExternalCompletionDialogOpen(true);
  };

  const handleSubmitExternalCompletion = async () => {
    if (!externalCompletionOrder?.id || !selectedApplication?.id) return;
    const trimmed = externalCompletionNotes.trim();
    if (trimmed.length < 3) {
      setSnackbar({ open: true, message: 'Add notes (at least 3 characters) describing how screening was completed.', severity: 'warning' });
      return;
    }
    try {
      setExternalCompletionSubmitting(true);
      await api.post(`/api/billing/screeningOrders/${externalCompletionOrder.id}/recordExternalCompletion`, {
        notes: trimmed,
        externalProviderName: externalCompletionProvider.trim() || undefined,
        reportUrl: externalCompletionReportUrl.trim() || undefined
      });
      setExternalCompletionDialogOpen(false);
      setSnackbar({ open: true, message: 'Screening recorded as completed (external).', severity: 'success' });
      const listRes = await api.get('/api/billing/screeningOrders', { params: { applicationId: selectedApplication.id } });
      setScreeningOrders(Array.isArray(listRes.data) ? listRes.data : []);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to record external screening',
        severity: 'error'
      });
    } finally {
      setExternalCompletionSubmitting(false);
    }
  };

  const toggleScreeningOrderActivity = async (orderId) => {
    const isExpanded = Boolean(expandedScreeningOrderEventsById[orderId]);
    if (isExpanded) {
      setExpandedScreeningOrderEventsById((prev) => ({ ...prev, [orderId]: false }));
      return;
    }
    setExpandedScreeningOrderEventsById((prev) => ({ ...prev, [orderId]: true }));
    if (screeningOrderEventsById[orderId]) return;
    try {
      setLoadingScreeningOrderEventsById((prev) => ({ ...prev, [orderId]: true }));
      const res = await api.get(`/api/billing/screeningOrders/${orderId}/events`, {
        params: { limit: SCREENING_ACTIVITY_PREVIEW_LIMIT }
      });
      const events = Array.isArray(res.data?.events) ? res.data.events : [];
      setScreeningOrderEventsById((prev) => ({ ...prev, [orderId]: events }));
      setScreeningOrderEventsPageById((prev) => ({
        ...prev,
        [orderId]: {
          hasMore: Boolean(res.data?.hasMore),
          nextBeforeCreatedAt: res.data?.nextBeforeCreatedAt || null
        }
      }));
    } catch (_) {
      setScreeningOrderEventsById((prev) => ({ ...prev, [orderId]: [] }));
      setSnackbar({ open: true, message: 'Unable to load screening activity right now.', severity: 'warning' });
    } finally {
      setLoadingScreeningOrderEventsById((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const openScreeningActivityDialog = async (orderId) => {
    setScreeningActivityDialogOrderId(orderId);
    setScreeningActivityDialogOpen(true);
    if (Array.isArray(screeningOrderEventsById[orderId]) && screeningOrderEventsById[orderId].length >= SCREENING_ACTIVITY_DIALOG_LIMIT) return;
    try {
      setLoadingScreeningOrderEventsById((prev) => ({ ...prev, [orderId]: true }));
      const res = await api.get(`/api/billing/screeningOrders/${orderId}/events`, {
        params: { limit: SCREENING_ACTIVITY_DIALOG_LIMIT }
      });
      const events = Array.isArray(res.data?.events) ? res.data.events : [];
      setScreeningOrderEventsById((prev) => ({ ...prev, [orderId]: events }));
      setScreeningOrderEventsPageById((prev) => ({
        ...prev,
        [orderId]: {
          hasMore: Boolean(res.data?.hasMore),
          nextBeforeCreatedAt: res.data?.nextBeforeCreatedAt || null
        }
      }));
    } catch (_) {
      setScreeningOrderEventsById((prev) => ({ ...prev, [orderId]: [] }));
      setSnackbar({ open: true, message: 'Unable to load screening activity right now.', severity: 'warning' });
    } finally {
      setLoadingScreeningOrderEventsById((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const loadMoreScreeningActivity = async (orderId) => {
    const page = screeningOrderEventsPageById[orderId] || {};
    if (!page.hasMore || !page.nextBeforeCreatedAt) return;
    try {
      setLoadingMoreScreeningOrderEventsById((prev) => ({ ...prev, [orderId]: true }));
      const res = await api.get(`/api/billing/screeningOrders/${orderId}/events`, {
        params: { limit: SCREENING_ACTIVITY_DIALOG_LIMIT, beforeCreatedAt: page.nextBeforeCreatedAt }
      });
      const olderEvents = Array.isArray(res.data?.events) ? res.data.events : [];
      setScreeningOrderEventsById((prev) => {
        const existing = Array.isArray(prev[orderId]) ? prev[orderId] : [];
        const merged = [...existing];
        olderEvents.forEach((event) => {
          if (!merged.some((item) => item.id === event.id)) merged.push(event);
        });
        return { ...prev, [orderId]: merged };
      });
      setScreeningOrderEventsPageById((prev) => ({
        ...prev,
        [orderId]: {
          hasMore: Boolean(res.data?.hasMore),
          nextBeforeCreatedAt: res.data?.nextBeforeCreatedAt || null
        }
      }));
    } catch (_) {
      setSnackbar({ open: true, message: 'Unable to load more activity right now.', severity: 'warning' });
    } finally {
      setLoadingMoreScreeningOrderEventsById((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const formatScreeningActivityEvent = (event) => {
    const typeLabel = event?.eventType?.replace(/_/g, ' ') || 'event';
    const transition = event?.priorStatus && event?.resultingStatus
      ? `${String(event.priorStatus).replace(/_/g, ' ')} -> ${String(event.resultingStatus).replace(/_/g, ' ')}`
      : null;
    const actor = event?.actorRole ? `${event.actorRole}${event.actorUid ? ` (${event.actorUid})` : ''}` : (event?.actorUid || null);
    const provider = event?.externalProviderName || null;
    const ledger = event?.usageLedgerId ? `Ledger: ${event.usageLedgerId}` : null;
    const parts = [typeLabel, transition, actor ? `By: ${actor}` : null, provider ? `Provider: ${provider}` : null, ledger]
      .filter(Boolean);
    return parts.join(' • ');
  };

  // Helper function to format phone number for tel: link
  const formatPhoneForDial = (phone) => {
    if (!phone) return null;
    // Remove all non-digit characters except +
    return phone.replace(/[^\d+]/g, '');
  };

  // Function to populate call scripts with applicant data
  const getPopulatedCallScript = (scriptType) => {
    if (!selectedApplication) return '';
    
    const applicant = selectedApplication.applicant || {};
    const previousTenancy = selectedApplication.applicantPreviousTenancy || {};
    const employment = selectedApplication.applicantEmployment || {};
    const applicantName = applicant.name || 'the applicant';
    const previousAddress = previousTenancy.previousAddress || '[Previous Address]';
    const timeAtLocation = previousTenancy.timeAtLocation || '[Time at Location]';
    const monthlyRent = previousTenancy.monthlyRent || '[Monthly Rent]';
    const employer = employment.employer || '[Company Name]';
    const jobPosition = employment.jobPosition || '[Job Position]';
    const lengthOfEmployment = employment.lengthOfEmployment || '[Length of Employment]';
    const monthlyIncome = employment.monthlyIncome || '[Monthly Income]';
    
    const scripts = {
      landlord: `LANDLORD REFERENCE CALL SCRIPT

Hello, this is calling from the property management office. I'm calling to verify rental history for ${applicantName} who listed you as their previous landlord.

1. Can you confirm that ${applicantName} rented from you at ${previousAddress}?
2. What were the dates of their tenancy? (They indicated: ${timeAtLocation})
3. What was the monthly rent amount? (They indicated: $${monthlyRent})
4. Did they pay rent on time consistently?
5. Did they maintain the property well?
6. Were there any issues during their tenancy?
7. Would you rent to them again?
8. Is there anything else I should know about them as a tenant?

Thank you for your time.`,

      employment: `EMPLOYMENT REFERENCE CALL SCRIPT

Hello, this is calling from the property management office. I'm calling to verify employment information for ${applicantName} who listed you as their employer/supervisor.

1. Can you confirm that ${applicantName} is currently employed at ${employer}?
2. What is their job title/position? (They indicated: ${jobPosition})
3. How long have they been employed there? (They indicated: ${lengthOfEmployment})
4. What is their current salary/monthly income? (They indicated: $${monthlyIncome}/month)
5. Is their employment status full-time, part-time, or contract?
6. Are they a reliable employee?
7. Is their employment likely to continue?
8. Is there anything else I should know about their employment status?

Thank you for your time.`,

      personal: `PERSONAL REFERENCE CALL SCRIPT

Hello, this is calling from the property management office. I'm calling to verify a personal reference for ${applicantName} who listed you as a reference.

1. How long have you known ${applicantName}?
2. What is your relationship to them?
3. How would you describe their character and reliability?
4. Do you know about their financial responsibility?
5. Would you trust them to be a responsible tenant?
6. Is there anything else I should know about them?

Thank you for your time.`
    };
    
    return scripts[scriptType] || '';
  };

  // Fetch notes for an application
  const fetchNotes = async (applicationId, propertyId, unitId) => {
    try {
      const response = await api.get(
        `/api/properties/${propertyId}/units/${unitId}/applications/${applicationId}/notes`
      );
      setNotes(response.data || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
      // If endpoint doesn't exist yet, initialize empty array
      setNotes([]);
    }
  };

  // Add a new note (with optional note text and type)
  const handleAddNote = async (noteText = null, noteType = 'general') => {
    const noteToAdd = noteText || newNote;
    if (!noteToAdd || !noteToAdd.trim() || !selectedApplication) return;
    
    try {
      const noteData = {
        note: noteToAdd.trim(),
        timestamp: new Date().toISOString(),
        type: noteType
      };
      
      await api.post(
        `/api/properties/${selectedApplication.propertyId}/units/${selectedApplication.unitId}/applications/${selectedApplication.id}/notes`,
        noteData
      );
      
      if (noteText) {
        setQuickNote(''); // Clear quick note if it was used
      } else {
        setNewNote(''); // Clear main note field
      }
      await fetchNotes(selectedApplication.id, selectedApplication.propertyId, selectedApplication.unitId);
      
      setSnackbar({
        open: true,
        message: 'Note added successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error adding note:', error);
      setSnackbar({
        open: true,
        message: 'Failed to add note',
        severity: 'error'
      });
    }
  };

  const handleStatusChange = async (applicationId, propertyId, unitId, newStatus) => {
    try {
      await api.patch(
        `/api/properties/${propertyId}/units/${unitId}/applications/${applicationId}`,
        { status: newStatus }
      );
      
      setSnackbar({
        open: true,
        message: `Application ${newStatus.toLowerCase()} successfully`,
        severity: 'success'
      });
      
      await fetchApplications();
      setDetailDialogOpen(false);
    } catch (error) {
      console.error('Error updating application status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update application status',
        severity: 'error'
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return 'success';
      case 'Rejected':
        return 'error';
      case 'Pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getScreeningColor = (tier) => {
    switch (tier) {
      case 'Strict':
        return 'error';
      case 'Standard':
        return 'warning';
      case 'Basic':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      let d;
      // Handle Firestore Timestamp with toDate method (client-side Firestore SDK)
      if (date && typeof date === 'object' && date.toDate && typeof date.toDate === 'function') {
        d = date.toDate();
      }
      // Handle Firestore Timestamp serialized format with _seconds and _nanoseconds (from API)
      else if (date && typeof date === 'object' && (date._seconds !== undefined || date.seconds !== undefined)) {
        const seconds = date._seconds || date.seconds || 0;
        const nanoseconds = date._nanoseconds || date.nanoseconds || 0;
        d = new Date(seconds * 1000 + nanoseconds / 1000000);
      }
      // Handle string date (ISO format or other)
      else if (typeof date === 'string') {
        d = new Date(date);
      }
      // Handle Date object
      else if (date instanceof Date) {
        d = date;
      }
      // Handle number (timestamp)
      else if (typeof date === 'number') {
        d = new Date(date);
      }
      // Try to convert directly
      else {
        d = new Date(date);
      }
      
      // Check if date is valid
      if (isNaN(d.getTime())) {
        console.warn('Invalid date value:', date);
        return 'Invalid Date';
      }
      
      return d.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      console.error('Error formatting date:', date, e);
      return 'Invalid Date';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const filteredApplications = applications.filter(app => {
    if (filterStatus !== 'all' && app.status !== filterStatus) return false;
    if (filterProperty !== 'all' && app.propertyId !== filterProperty) return false;
    if (filterSignature !== 'all') {
      const sigStatus = getSignatureStatus(app);
      if (filterSignature === 'not_required' && sigStatus !== 'not_required') return false;
      if (filterSignature === 'pending' && sigStatus !== 'pending') return false;
      if (filterSignature === 'signed' && sigStatus !== 'signed') return false;
    }
    return true;
  });

  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'Pending').length,
    approved: applications.filter(a => a.status === 'Approved').length,
    rejected: applications.filter(a => a.status === 'Rejected').length
  };

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2, md: 3 },
      width: '100%',
      maxWidth: '100%',
      overflowX: 'hidden',
      boxSizing: 'border-box',
      position: 'relative',
      minWidth: 0
    }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        mb: { xs: 2, sm: 3 },
        gap: { xs: 1.5, sm: 0 },
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
        position: 'relative'
      }}>
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{ 
            fontSize: { xs: '1.25rem', sm: '2rem' }, 
            mb: { xs: 0, sm: 0 },
            lineHeight: { xs: 1.3, sm: 1.5 },
            wordBreak: 'break-word',
            width: { xs: '100%', sm: 'auto' },
            flexShrink: 0,
            minWidth: 0,
            maxWidth: { xs: '100%', sm: 'none' }
          }}
        >
          <AssignmentIcon sx={{ mr: { xs: 0.5, sm: 1 }, verticalAlign: 'middle', fontSize: { xs: '1.25rem', sm: 'inherit' } }} />
          {isMobile ? 'Rental Applications' : 'Rental Application Management'}
        </Typography>
        <Box 
          className="rental-app-buttons-wrapper"
          sx={{ 
            width: { xs: '100%', sm: 'auto' },
            maxWidth: { xs: '100%', sm: 'none' },
            minWidth: { xs: 0, sm: 'auto' },
            display: { xs: 'block', sm: 'flex' },
            gap: { xs: 0, sm: 1 },
            flexDirection: { xs: 'column', sm: 'row' },
            flexShrink: { xs: 1, sm: 0 },
            flexGrow: { xs: 0, sm: 0 },
            boxSizing: 'border-box',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ display: { xs: 'none', sm: 'inline-flex' } }} />}
            onClick={() => setFormDialogOpen(true)}
            size="small"
            className="rental-app-button-mobile"
            sx={{ 
              width: { xs: '100%', sm: 'auto' },
              mb: { xs: 1, sm: 0 },
              minWidth: { xs: '0 !important', sm: 64 },
              maxWidth: { xs: '100% !important', sm: 'none' },
              '&.MuiButton-root': {
                '@media (max-width: 600px)': {
                  width: '100% !important',
                  minWidth: '0 !important',
                  maxWidth: '100% !important'
                }
              }
            }}
          >
            {isMobile ? 'New' : 'New Application'}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<LinkIcon sx={{ display: { xs: 'none', sm: 'inline-flex' } }} />}
            onClick={() => {
              setLinkDialogOpen(true);
              setSelectedPropertyForLink('');
              setSelectedUnitForLink('');
              setAvailableUnitsForLink([]);
              fetchLandlordSlug();
            }}
            size="small"
            className="rental-app-button-mobile"
            sx={{ 
              width: { xs: '100%', sm: 'auto' },
              mb: { xs: 1, sm: 0 },
              minWidth: { xs: '0 !important', sm: 64 },
              maxWidth: { xs: '100% !important', sm: 'none' },
              '&.MuiButton-root': {
                '@media (max-width: 600px)': {
                  width: '100% !important',
                  minWidth: '0 !important',
                  maxWidth: '100% !important'
                }
              }
            }}
          >
            {isMobile ? 'Link' : 'Generate Public Link'}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon sx={{ display: { xs: 'none', sm: 'inline-flex' } }} />}
            onClick={fetchApplications}
            disabled={loading}
            size="small"
            className="rental-app-button-mobile"
            sx={{ 
              width: { xs: '100%', sm: 'auto' },
              minWidth: { xs: '0 !important', sm: 64 },
              maxWidth: { xs: '100% !important', sm: 'none' },
              '&.MuiButton-root': {
                '@media (max-width: 600px)': {
                  width: '100% !important',
                  minWidth: '0 !important',
                  maxWidth: '100% !important'
                }
              }
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
              <Typography 
                color="textSecondary" 
                gutterBottom
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: { xs: 0.5, sm: 1 } }}
              >
                Total
              </Typography>
              <Typography 
                variant="h4"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, lineHeight: 1.2 }}
              >
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
              <Typography 
                color="textSecondary" 
                gutterBottom
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: { xs: 0.5, sm: 1 } }}
              >
                Pending
              </Typography>
              <Typography 
                variant="h4" 
                color="warning.main"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, lineHeight: 1.2 }}
              >
                {stats.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
              <Typography 
                color="textSecondary" 
                gutterBottom
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: { xs: 0.5, sm: 1 } }}
              >
                Approved
              </Typography>
              <Typography 
                variant="h4" 
                color="success.main"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, lineHeight: 1.2 }}
              >
                {stats.approved}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
              <Typography 
                color="textSecondary" 
                gutterBottom
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: { xs: 0.5, sm: 1 } }}
              >
                Rejected
              </Typography>
              <Typography 
                variant="h4" 
                color="error.main"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, lineHeight: 1.2 }}
              >
                {stats.rejected}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: { xs: 2, sm: 3 }, width: '100%', maxWidth: '100%' }}>
        <CardContent sx={{ 
          px: { xs: 1.5, sm: 2 },
          pt: { xs: 2, sm: 2.5 },
          pb: { xs: 1.5, sm: 2 },
          '&:last-child': { pb: { xs: 1.5, sm: 2 } },
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 1.5, sm: 2 }, 
            alignItems: { xs: 'stretch', sm: 'center' }, 
            flexWrap: 'wrap',
            flexDirection: { xs: 'column', sm: 'row' },
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}>
            <FilterListIcon sx={{ display: { xs: 'none', sm: 'block' }, color: 'text.secondary', flexShrink: 0 }} />
            <FormControl sx={{ minWidth: { xs: '100%', sm: 150 }, flex: { sm: '0 1 160px' } }} size="small">
              <InputLabel id="filter-status-label" shrink>
                Status
              </InputLabel>
              <Select
                labelId="filter-status-label"
                value={filterStatus}
                label="Status"
                notched
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Approved">Approved</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: { xs: '100%', sm: 200 }, flex: { sm: '0 1 220px' } }} size="small">
              <InputLabel id="filter-property-label" shrink>
                Property
              </InputLabel>
              <Select
                labelId="filter-property-label"
                value={filterProperty}
                label="Property"
                notched
                onChange={(e) => setFilterProperty(e.target.value)}
              >
                <MenuItem value="all">All Properties</MenuItem>
                {properties.map((property) => (
                  <MenuItem key={property.id} value={property.id}>
                    {property.name || property.address}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: { xs: '100%', sm: 180 }, flex: { sm: '0 1 200px' } }} size="small">
              <InputLabel id="filter-signature-label" shrink>
                {isMobile ? 'E-Signature' : 'Credit Consent'}
              </InputLabel>
              <Select
                labelId="filter-signature-label"
                value={filterSignature}
                label={isMobile ? 'E-Signature' : 'Credit Consent'}
                notched
                onChange={(e) => setFilterSignature(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="not_required">Not Required</MenuItem>
                <MenuItem value="pending">Pending Signature</MenuItem>
                <MenuItem value="signed">Signed</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Applications Table */}
      <Card sx={{ overflow: 'hidden' }}>
        <CardContent sx={{ 
          p: { xs: 1, sm: 2 }, 
          '&:last-child': { pb: { xs: 1, sm: 2 } },
          overflow: 'hidden'
        }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 2, sm: 4 } }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ 
              width: '100%',
              overflow: 'hidden',
              mx: { xs: -1, sm: 0 }, // Negative margin to extend scroll area to card edges on mobile
            }}>
              <TableContainer 
                component={Paper}
                elevation={0}
                sx={{ 
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  width: '100%',
                  maxWidth: '100%',
                  WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
                  '&::-webkit-scrollbar': {
                    height: { xs: 6, sm: 12 },
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    borderRadius: 4,
                  },
                }}
              >
                <Table 
                  sx={{ 
                    minWidth: { xs: 1100, sm: 'auto' },
                    tableLayout: { xs: 'fixed', sm: 'auto' },
                    width: '100%',
                  }}
                >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      fontSize: { xs: '0.7rem', sm: '0.875rem' },
                      minWidth: { xs: 100, sm: 'auto' },
                      width: { xs: '12%', sm: 'auto' },
                      px: { xs: 0.5, sm: 1 }
                    }}>
                      Applicant
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: { xs: '0.7rem', sm: '0.875rem' },
                      minWidth: { xs: 80, sm: 'auto' },
                      width: { xs: '10%', sm: 'auto' },
                      px: { xs: 0.5, sm: 1 },
                      display: { xs: 'none', md: 'table-cell' }
                    }}>
                      Co-Applicant
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: { xs: '0.7rem', sm: '0.875rem' },
                      minWidth: { xs: 120, sm: 'auto' },
                      width: { xs: '15%', sm: 'auto' },
                      px: { xs: 0.5, sm: 1 }
                    }}>
                      Contact
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: { xs: '0.7rem', sm: '0.875rem' },
                      minWidth: { xs: 80, sm: 'auto' },
                      width: { xs: '10%', sm: 'auto' },
                      px: { xs: 0.5, sm: 1 },
                      display: { xs: 'none', lg: 'table-cell' }
                    }}>
                      Suite
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: { xs: '0.7rem', sm: '0.875rem' },
                      minWidth: { xs: 100, sm: 'auto' },
                      width: { xs: '12%', sm: 'auto' },
                      px: { xs: 0.5, sm: 1 },
                      display: { xs: 'none', lg: 'table-cell' }
                    }}>
                      Employment
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: { xs: '0.7rem', sm: '0.875rem' },
                      minWidth: { xs: 80, sm: 'auto' },
                      width: { xs: '10%', sm: 'auto' },
                      px: { xs: 0.5, sm: 1 },
                      display: { xs: 'none', lg: 'table-cell' }
                    }}>
                      Income
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: { xs: '0.7rem', sm: '0.875rem' },
                      minWidth: { xs: 80, sm: 'auto' },
                      width: { xs: '10%', sm: 'auto' },
                      px: { xs: 0.5, sm: 1 },
                      display: { xs: 'none', md: 'table-cell' }
                    }}>
                      Credit
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: { xs: '0.7rem', sm: '0.875rem' },
                      minWidth: { xs: 90, sm: 'auto' },
                      width: { xs: '12%', sm: 'auto' },
                      px: { xs: 0.5, sm: 1 },
                      display: { xs: 'none', md: 'table-cell' }
                    }}>
                      Risk Score
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: { xs: '0.7rem', sm: '0.875rem' },
                      minWidth: { xs: 100, sm: 'auto' },
                      width: { xs: '12%', sm: 'auto' },
                      px: { xs: 0.5, sm: 1 }
                    }}>
                      {isMobile ? 'E-Sign' : 'Credit Consent'}
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: { xs: '0.7rem', sm: '0.875rem' },
                      minWidth: { xs: 70, sm: 'auto' },
                      width: { xs: '10%', sm: 'auto' },
                      px: { xs: 0.5, sm: 1 }
                    }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: { xs: '0.7rem', sm: '0.875rem' },
                      minWidth: { xs: 80, sm: 'auto' },
                      width: { xs: '10%', sm: 'auto' },
                      px: { xs: 0.5, sm: 1 },
                      display: { xs: 'none', md: 'table-cell' }
                    }}>
                      Submitted
                    </TableCell>
                    <TableCell 
                      align="right" 
                      sx={{ 
                        fontSize: { xs: '0.7rem', sm: '0.875rem' },
                        minWidth: { xs: 80, sm: 'auto' },
                        width: { xs: '10%', sm: 'auto' },
                        px: { xs: 0.5, sm: 1 },
                        position: { xs: 'sticky', sm: 'static' },
                        right: { xs: 0, sm: 'auto' },
                        backgroundColor: { xs: 'background.paper', sm: 'transparent' },
                        zIndex: { xs: 1, sm: 0 }
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredApplications.length === 0 ? (
                    <TableRow>
                      <TableCell 
                        colSpan={isMobile ? 6 : 13} 
                        align="center" 
                        sx={{ py: { xs: 3, sm: 4 } }}
                      >
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                        >
                          No applications found. {filterStatus !== 'all' || filterProperty !== 'all' || filterSignature !== 'all'
                            ? 'Try adjusting your filters.' 
                            : 'Applications will appear here when submitted.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredApplications.map((application) => {
                    const applicant = application.applicant || {};
                    const coApplicant = application.coApplicant || null;
                    const employment = application.applicantEmployment || {};
                    const creditHistory = application.applicantCreditHistory || {};
                    const hasCreditIssues = creditHistory.declaredBankruptcy || creditHistory.everEvicted || creditHistory.latePayments || creditHistory.refusedRent;
                    const screeningScore = application.screeningScore ?? application.screening?.outputs?.score;
                    const screeningTier = application.screeningTier ?? application.screening?.outputs?.tier;
                    
                    return (
                    <TableRow key={application.id} hover>
                      <TableCell sx={{ px: { xs: 0.5, sm: 1 } }}>
                        <Typography 
                          variant="body2" 
                          fontWeight="medium"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          {applicant.name || application.name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ 
                        px: { xs: 0.5, sm: 1 },
                        display: { xs: 'none', md: 'table-cell' }
                      }}>
                        {coApplicant?.name ? (
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                          >
                            {coApplicant.name}
                          </Typography>
                        ) : (
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                          >
                            None
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ px: { xs: 0.5, sm: 1 } }}>
                        <Typography 
                          variant="body2"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          {applicant.email || application.email || 'N/A'}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                        >
                          {applicant.phone1 || application.phone || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ 
                        px: { xs: 0.5, sm: 1 },
                        display: { xs: 'none', lg: 'table-cell' }
                      }}>
                        <Typography 
                          variant="body2"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                        >
                          {application.suite || application.propertyName || 'N/A'}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                        >
                          {application.unitName || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ 
                        px: { xs: 0.5, sm: 1 },
                        display: { xs: 'none', lg: 'table-cell' }
                      }}>
                        <Typography 
                          variant="body2"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                        >
                          {employment.employer || application.employment || 'N/A'}
                        </Typography>
                        {employment.status && (
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                          >
                            {employment.status}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ 
                        px: { xs: 0.5, sm: 1 },
                        display: { xs: 'none', lg: 'table-cell' }
                      }}>
                        <Typography 
                          variant="body2"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                        >
                          {formatCurrency(employment.monthlyIncome || application.income)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ 
                        px: { xs: 0.5, sm: 1 },
                        display: { xs: 'none', md: 'table-cell' }
                      }}>
                        {hasCreditIssues ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {creditHistory.declaredBankruptcy && <Chip label="BK" size="small" color="error" sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }} />}
                            {creditHistory.everEvicted && <Chip label="EV" size="small" color="error" sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }} />}
                            {creditHistory.latePayments && <Chip label="LP" size="small" color="warning" sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }} />}
                            {creditHistory.refusedRent && <Chip label="RR" size="small" color="error" sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }} />}
                          </Box>
                        ) : (
                          <Chip label="Clean" size="small" color="success" sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }} />
                        )}
                      </TableCell>
                      <TableCell sx={{ 
                        px: { xs: 0.5, sm: 1 },
                        display: { xs: 'none', md: 'table-cell' }
                      }}>
                        {screeningScore !== null && screeningScore !== undefined ? (
                          <Chip
                            label={`${screeningTier || 'Score'} ${screeningScore}`}
                            color={getScreeningColor(screeningTier)}
                            size="small"
                            sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">N/A</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ px: { xs: 0.5, sm: 1 } }}>
                        {(() => {
                          const sigStatus = getSignatureStatus(application);
                          return (
                            <Chip
                              label={getSignatureStatusLabel(sigStatus)}
                              color={getSignatureStatusColor(sigStatus)}
                              size="small"
                              icon={sigStatus === 'signed' ? <CheckCircleIcon /> : null}
                              sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ px: { xs: 0.5, sm: 1 } }}>
                        <Chip
                          label={application.status}
                          color={getStatusColor(application.status)}
                          size="small"
                          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ 
                        px: { xs: 0.5, sm: 1 },
                        display: { xs: 'none', md: 'table-cell' }
                      }}>
                        <Typography 
                          variant="caption"
                          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                        >
                          {formatDate(application.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell 
                        align="right"
                        sx={{ 
                          px: { xs: 0.5, sm: 1 },
                          position: { xs: 'sticky', sm: 'static' },
                          right: { xs: 0, sm: 'auto' },
                          backgroundColor: { xs: 'background.paper', sm: 'transparent' },
                          zIndex: { xs: 1, sm: 0 }
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: { xs: 0.25, sm: 0.5 }, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size={isMobile ? 'small' : 'medium'}
                              onClick={() => handleViewDetails(application)}
                              aria-label="View application details"
                            >
                              <VisibilityIcon fontSize={isMobile ? 'small' : 'medium'} />
                            </IconButton>
                          </Tooltip>
                          {(() => {
                            const sigStatus = getSignatureStatus(application);
                            const sigData = signatureData.get(application.id);
                            if (sigStatus === 'signed' && application.creditConsentSignatureRequestId) {
                              return (
                                <>
                                  <Tooltip title="View Signed PDF">
                                    <IconButton
                                      size={isMobile ? 'small' : 'medium'}
                                      onClick={() => handleViewSignedPDF(application.creditConsentSignatureRequestId)}
                                      aria-label="View signed PDF"
                                    >
                                      <PdfIcon fontSize={isMobile ? 'small' : 'medium'} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Download Signed PDF">
                                    <IconButton
                                      size={isMobile ? 'small' : 'medium'}
                                      onClick={() => handleDownloadSignedPDF(application.creditConsentSignatureRequestId)}
                                      aria-label="Download signed PDF"
                                    >
                                      <DownloadIcon fontSize={isMobile ? 'small' : 'medium'} />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              );
                            } else if (sigStatus === 'pending' && application.creditConsentSignatureRequestId) {
                              return (
                                <Tooltip title="Resend Signature Email">
                                  <IconButton
                                    size={isMobile ? 'small' : 'medium'}
                                    onClick={() => handleResendSignatureEmail(application)}
                                    aria-label="Resend signature email"
                                  >
                                    <EmailIcon fontSize={isMobile ? 'small' : 'medium'} />
                                  </IconButton>
                                </Tooltip>
                              );
                            }
                            return null;
                          })()}
                          {application.status === 'Pending' && (
                            <>
                              <Tooltip title="Approve">
                                <IconButton
                                  size={isMobile ? 'small' : 'medium'}
                                  color="success"
                                  onClick={() => handleStatusChange(
                                    application.id,
                                    application.propertyId,
                                    application.unitId,
                                    'Approved'
                                  )}
                                  aria-label="Approve application"
                                >
                                  <CheckCircleIcon fontSize={isMobile ? 'small' : 'medium'} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <IconButton
                                  size={isMobile ? 'small' : 'medium'}
                                  color="error"
                                  onClick={() => handleStatusChange(
                                    application.id,
                                    application.propertyId,
                                    application.unitId,
                                    'Rejected'
                                  )}
                                  aria-label="Reject application"
                                >
                                  <CancelIcon fontSize={isMobile ? 'small' : 'medium'} />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    );
                  }))}
                </TableBody>
              </Table>
            </TableContainer>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Application Details Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Application Details
          {selectedApplication && (
            <Chip
              label={selectedApplication.status}
              color={getStatusColor(selectedApplication.status)}
              sx={{ ml: 2 }}
            />
          )}
          {selectedApplication?.applicationReference && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              Ref: {selectedApplication.applicationReference}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          {selectedApplication && (
            <Box sx={{ mt: 2 }}>
              {/* Section 1: Privacy & Consent */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>1. Privacy & Consent</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Privacy Policy Acknowledged</Typography>
                  <Typography variant="body1">{selectedApplication.privacyPolicyAcknowledged ? 'Yes' : 'No'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Terms Accepted</Typography>
                  <Typography variant="body1">{selectedApplication.termsAccepted ? 'Yes' : 'No'}</Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />

              {/* Section 2: Property Selection */}
              <Typography variant="h6" gutterBottom>2. Property Selection</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Suite</Typography>
                  <Typography variant="body1">{selectedApplication.suite || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Date of Occupancy Desired</Typography>
                  <Typography variant="body1">{selectedApplication.dateOfOccupancyDesired || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Number of Adults</Typography>
                  <Typography variant="body1">{selectedApplication.numberOfAdults || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Number of Children</Typography>
                  <Typography variant="body1">{selectedApplication.numberOfChildren || 0}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Ages of Children</Typography>
                  <Typography variant="body1">{selectedApplication.agesOfChildren || 'N/A'}</Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />

              {/* Screening Summary */}
              <Typography variant="h6" gutterBottom>Screening Summary</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Risk Score</Typography>
                  <Typography variant="body1">
                    {selectedApplication.screeningScore ?? selectedApplication.screening?.outputs?.score ?? 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Tier</Typography>
                  <Typography variant="body1">
                    {selectedApplication.screeningTier ?? selectedApplication.screening?.outputs?.tier ?? 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Income Ratio</Typography>
                  <Typography variant="body1">
                    {selectedApplication.screening?.incomeRatio ? `${selectedApplication.screening.incomeRatio}x` : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
              {selectedApplication.screening?.criteriaFlags?.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">Criteria Flags</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                    {selectedApplication.screening.criteriaFlags.map((flag) => (
                      <Chip key={flag} label={flag} size="small" color="warning" />
                    ))}
                  </Box>
                </Box>
              )}
              {selectedApplication.screening?.outputs?.verificationPriorities?.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">Verification Priorities</Typography>
                  <List dense>
                    {selectedApplication.screening.outputs.verificationPriorities.map((item) => (
                      <ListItem key={item} sx={{ py: 0 }}>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
              {selectedApplication.screening?.outputs?.decisionTree && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">Recommendation</Typography>
                  <Typography variant="body1">{selectedApplication.screening.outputs.decisionTree.recommendation}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedApplication.screening.outputs.decisionTree.reasoning}
                  </Typography>
                </Box>
              )}
              <Divider sx={{ my: 2 }} />

              {/* Credit check */}
              <Typography variant="h6" gutterBottom>Credit Check</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" data-testid="credit-check-guidance">
                    Run the credit check in FrontLobby. Use Show next to SIN/SSN above to copy the
                    applicant's SIN, or Request SIN to ask them for it.
                  </Typography>
                </Grid>
                {selectedApplication.backgroundCheckReportUrl && (
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      href={selectedApplication.backgroundCheckReportUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                    >
                      View Report
                    </Button>
                  </Grid>
                )}
                <Grid item xs={12}>
                  {screeningSetup?.showProviderComparisonLink ? (
                    <Button
                      component={Link}
                      to={`/screening/compare?applicationId=${encodeURIComponent(selectedApplication.id)}&paidBy=applicant`}
                      variant="outlined"
                      color="primary"
                      size="small"
                    >
                      Request screening (tenant screening)
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={openRequestScreeningDialog}
                    >
                      Request screening (tenant screening)
                    </Button>
                  )}
                </Grid>
              </Grid>

              {/* Tenant screening orders: status + View report */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Tenant screening</Typography>
              {loadingScreeningOrders ? (
                <Box sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">Loading screening orders…</Typography>
                </Box>
              ) : screeningOrders.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No screening orders for this application yet.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {screeningOrders.map((order, index) => {
                    const status = order.status || 'invited';
                    const statusLabel = status.replace(/_/g, ' ');
                    const isComplete = status === 'complete';
                    const isFailed = status === 'failed';
                    const isCanceled = status === 'canceled';
                    const reportUrl = order.reportRef && (order.reportRef.startsWith('http') ? order.reportRef : null) || order.reportUrl || null;
                    const createdAt = order.createdAt ? new Date(order.createdAt).toLocaleDateString(undefined, { dateStyle: 'short' }) : '';
                    const orderIdDisplay = order.id || `#${index + 1}`;
                    const canRecordExternal = !isComplete && !isCanceled;
                    const extNotes = order.externalCompletion?.notes;
                    const activityEvents = screeningOrderEventsById[order.id] || [];
                    const activityPage = screeningOrderEventsPageById[order.id] || { hasMore: false };
                    const isActivityExpanded = Boolean(expandedScreeningOrderEventsById[order.id]);
                    const isActivityLoading = Boolean(loadingScreeningOrderEventsById[order.id]);
                    return (
                      <Card key={order.id} variant="outlined" sx={{ p: 1.5 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={statusLabel}
                            size="small"
                            color={isComplete ? 'success' : isFailed ? 'error' : 'default'}
                          />
                          {order.fulfillmentMode === 'external_manual' && (
                            <Chip label="External screening" size="small" variant="outlined" color="secondary" />
                          )}
                          {order.sku && <Typography variant="caption" color="text.secondary">{order.sku}</Typography>}
                          {order.paidBy && <Typography variant="caption" color="text.secondary">Paid by: {order.paidBy}</Typography>}
                          {createdAt && <Typography variant="caption" color="text.secondary">{createdAt}</Typography>}
                          {isComplete && reportUrl && (
                            <Button
                              component="a"
                              href={reportUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              size="small"
                              variant="outlined"
                            >
                              View report
                            </Button>
                          )}
                          {isComplete && !reportUrl && (
                            <Typography variant="caption" color="text.secondary">Report ready (no link)</Typography>
                          )}
                          {canRecordExternal && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              onClick={() => openExternalCompletionDialog(order)}
                            >
                              Record external screening
                            </Button>
                          )}
                          <Typography variant="caption" sx={{ fontWeight: 600 }} title={order.id}>
                            Order: {orderIdDisplay}
                          </Typography>
                          {order.id && (
                            <Button
                              size="small"
                              variant="text"
                              endIcon={<ExpandMoreIcon sx={{ transform: isActivityExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }} />}
                              onClick={() => toggleScreeningOrderActivity(order.id)}
                            >
                              Activity
                            </Button>
                          )}
                        </Box>
                        {isComplete && extNotes && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            External notes: {extNotes}
                          </Typography>
                        )}
                        <Collapse in={isActivityExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ mt: 1, borderTop: '1px dashed', borderColor: 'divider', pt: 1 }}>
                            {isActivityLoading ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={14} />
                                <Typography variant="caption" color="text.secondary">Loading activity…</Typography>
                              </Box>
                            ) : activityEvents.length === 0 ? (
                              <Typography variant="caption" color="text.secondary">No activity recorded yet.</Typography>
                            ) : (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {activityEvents.map((event) => (
                                  <Box key={event.id} sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant="caption" color="text.secondary">
                                      {formatScreeningActivityEvent(event)}
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled">
                                      {event.createdAt ? new Date(event.createdAt).toLocaleString() : 'time unavailable'}
                                    </Typography>
                                  </Box>
                                ))}
                                {(activityEvents.length >= SCREENING_ACTIVITY_PREVIEW_LIMIT || activityPage.hasMore) && (
                                  <Box sx={{ mt: 0.5 }}>
                                    <Button size="small" variant="text" onClick={() => openScreeningActivityDialog(order.id)}>
                                      View all activity
                                    </Button>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </Card>
                    );
                  })}
                </Box>
              )}
              <Divider sx={{ my: 2 }} />

              {/* Section 3: Applicant Personal Information */}
              <Typography variant="h6" gutterBottom>3. Applicant Personal Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Name</Typography>
                  <Typography variant="body1">{selectedApplication.applicant?.name || selectedApplication.name || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedApplication.applicant?.email || selectedApplication.email || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Current Address</Typography>
                  <Typography variant="body1">{selectedApplication.applicant?.currentAddress || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Phone 1</Typography>
                  {selectedApplication.applicant?.phone1 || selectedApplication.phone ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                        component="a" 
                        href={`tel:${formatPhoneForDial(selectedApplication.applicant?.phone1 || selectedApplication.phone)}`}
                        variant="body1"
                        sx={{ 
                          textDecoration: 'none', 
                          color: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        <PhoneIcon fontSize="small" />
                        {selectedApplication.applicant?.phone1 || selectedApplication.phone}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body1">N/A</Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Phone 2</Typography>
                  {selectedApplication.applicant?.phone2 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                        component="a" 
                        href={`tel:${formatPhoneForDial(selectedApplication.applicant?.phone2)}`}
                        variant="body1"
                        sx={{ 
                          textDecoration: 'none', 
                          color: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        <PhoneIcon fontSize="small" />
                        {selectedApplication.applicant?.phone2}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body1">N/A</Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Date of Birth</Typography>
                  <Typography variant="body1">{selectedApplication.applicant?.dateOfBirth || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ApplicantSinField application={selectedApplication} role="applicant" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Driver's License</Typography>
                  <Typography variant="body1">{selectedApplication.applicant?.driversLicense || 'Not provided'}</Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />

              {/* Section 4: Co-Applicant (if exists) */}
              {selectedApplication.coApplicant?.name && (
                <>
                  <Typography variant="h6" gutterBottom>4. Co-Applicant Personal Information</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Name</Typography>
                      <Typography variant="body1">{selectedApplication.coApplicant.name}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Email</Typography>
                      <Typography variant="body1">{selectedApplication.coApplicant.email || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Phone 1</Typography>
                      {selectedApplication.coApplicant.phone1 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography 
                            component="a" 
                            href={`tel:${formatPhoneForDial(selectedApplication.coApplicant.phone1)}`}
                            variant="body1"
                            sx={{ 
                              textDecoration: 'none', 
                              color: 'primary.main',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}
                          >
                            <PhoneIcon fontSize="small" />
                            {selectedApplication.coApplicant.phone1}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body1">N/A</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Date of Birth</Typography>
                      <Typography variant="body1">{selectedApplication.coApplicant.dateOfBirth || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ApplicantSinField application={selectedApplication} role="coApplicant" />
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {/* Section 5: Previous Tenancy */}
              <Typography variant="h6" gutterBottom>5. Previous Tenancy - Applicant</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Landlord's Name</Typography>
                  <Typography variant="body1">{selectedApplication.applicantPreviousTenancy?.landlordName || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Landlord's Phone</Typography>
                  {selectedApplication.applicantPreviousTenancy?.landlordPhone ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                        component="a" 
                        href={`tel:${formatPhoneForDial(selectedApplication.applicantPreviousTenancy?.landlordPhone)}`}
                        variant="body1"
                        sx={{ 
                          textDecoration: 'none', 
                          color: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        <PhoneIcon fontSize="small" />
                        {selectedApplication.applicantPreviousTenancy?.landlordPhone}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setShowCallScript(showCallScript === 'landlord' ? null : 'landlord')}
                        sx={{ ml: 1 }}
                      >
                        View Script
                      </Button>
                    </Box>
                  ) : (
                    <Typography variant="body1">N/A</Typography>
                  )}
                </Grid>
                {showCallScript === 'landlord' && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Landlord Reference Call Script
                      </Typography>
                      <Typography 
                        variant="body2" 
                        component="pre" 
                        sx={{ 
                          whiteSpace: 'pre-wrap', 
                          fontFamily: 'inherit',
                          fontSize: '0.875rem',
                          margin: 0,
                          mt: 1
                        }}
                      >
                        {getPopulatedCallScript('landlord')}
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          Quick Note (Landlord Reference Call)
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          size="small"
                          placeholder="Add notes from this call..."
                          value={quickNote}
                          onChange={(e) => setQuickNote(e.target.value)}
                          sx={{ mb: 1, mt: 1 }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleAddNote(quickNote, 'reference_call')}
                          disabled={!quickNote.trim()}
                          startIcon={<NoteIcon />}
                        >
                          Save Note
                        </Button>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setShowCallScript(null)}
                        sx={{ mt: 1 }}
                      >
                        Close Script
                      </Button>
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Previous Address</Typography>
                  <Typography variant="body1">{selectedApplication.applicantPreviousTenancy?.previousAddress || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Time at Location</Typography>
                  <Typography variant="body1">{selectedApplication.applicantPreviousTenancy?.timeAtLocation || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Monthly Rent</Typography>
                  <Typography variant="body1">{formatCurrency(selectedApplication.applicantPreviousTenancy?.monthlyRent)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Reason for Leaving</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{selectedApplication.applicantPreviousTenancy?.reasonForLeaving || 'N/A'}</Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />

              {/* Section 7: Employment */}
              <Typography variant="h6" gutterBottom>7. Employment Information - Applicant</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Typography variant="body1">{selectedApplication.applicantEmployment?.status || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Employer</Typography>
                  <Typography variant="body1">{selectedApplication.applicantEmployment?.employer || selectedApplication.employment || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Job Position</Typography>
                  <Typography variant="body1">{selectedApplication.applicantEmployment?.jobPosition || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Monthly Income</Typography>
                  <Typography variant="body1">{formatCurrency(selectedApplication.applicantEmployment?.monthlyIncome || selectedApplication.income)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Length of Employment</Typography>
                  <Typography variant="body1">{selectedApplication.applicantEmployment?.lengthOfEmployment || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Supervisor</Typography>
                  <Typography variant="body1">{selectedApplication.applicantEmployment?.supervisorName || 'N/A'}</Typography>
                </Grid>
                {selectedApplication.applicantEmployment?.supervisorPhone && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Supervisor Phone</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                        component="a" 
                        href={`tel:${formatPhoneForDial(selectedApplication.applicantEmployment?.supervisorPhone)}`}
                        variant="body1"
                        sx={{ 
                          textDecoration: 'none', 
                          color: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        <PhoneIcon fontSize="small" />
                        {selectedApplication.applicantEmployment?.supervisorPhone}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setShowCallScript(showCallScript === 'employment' ? null : 'employment')}
                        sx={{ ml: 1 }}
                      >
                        View Script
                      </Button>
                    </Box>
                  </Grid>
                )}
                {showCallScript === 'employment' && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Employment Reference Call Script
                      </Typography>
                      <Typography 
                        variant="body2" 
                        component="pre" 
                        sx={{ 
                          whiteSpace: 'pre-wrap', 
                          fontFamily: 'inherit',
                          fontSize: '0.875rem',
                          margin: 0,
                          mt: 1
                        }}
                      >
                        {getPopulatedCallScript('employment')}
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          Quick Note (Employment Verification)
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          size="small"
                          placeholder="Add notes from this call..."
                          value={quickNote}
                          onChange={(e) => setQuickNote(e.target.value)}
                          sx={{ mb: 1, mt: 1 }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleAddNote(quickNote, 'employment_verification')}
                          disabled={!quickNote.trim()}
                          startIcon={<NoteIcon />}
                        >
                          Save Note
                        </Button>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setShowCallScript(null)}
                        sx={{ mt: 1 }}
                      >
                        Close Script
                      </Button>
                    </Alert>
                  </Grid>
                )}
              </Grid>
              <Divider sx={{ my: 2 }} />

              {/* Section 9: Credit History */}
              <Typography variant="h6" gutterBottom>9. Credit History & Rental Background - Applicant</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Declared Bankruptcy (past 7 years)</Typography>
                  <Typography variant="body1">{selectedApplication.applicantCreditHistory?.declaredBankruptcy ? 'Yes' : 'No'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Ever Evicted</Typography>
                  <Typography variant="body1">{selectedApplication.applicantCreditHistory?.everEvicted ? 'Yes' : 'No'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Late Payments (2+ in past 12 months)</Typography>
                  <Typography variant="body1">{selectedApplication.applicantCreditHistory?.latePayments ? 'Yes' : 'No'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Refused to Pay Rent</Typography>
                  <Typography variant="body1">{selectedApplication.applicantCreditHistory?.refusedRent ? 'Yes' : 'No'}</Typography>
                </Grid>
                {selectedApplication.applicantCreditHistory?.explanation && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Explanation</Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{selectedApplication.applicantCreditHistory.explanation}</Typography>
                  </Grid>
                )}
              </Grid>
              <Divider sx={{ my: 2 }} />

              {/* Section 11: Additional Information */}
              <Typography variant="h6" gutterBottom>11. Additional Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Has Pets</Typography>
                  <Typography variant="body1">{selectedApplication.additionalInfo?.hasPets ? 'Yes' : 'No'}</Typography>
                </Grid>
                {selectedApplication.additionalInfo?.hasPets && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Pet Description</Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{selectedApplication.additionalInfo.petDescription || 'N/A'}</Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Smoking</Typography>
                  <Typography variant="body1">{selectedApplication.additionalInfo?.smoking ? 'Yes' : 'No'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Credit Report Consent</Typography>
                  <Typography variant="body1">{selectedApplication.additionalInfo?.creditReportConsent ? 'Yes' : 'No'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Has Insurance</Typography>
                  <Typography variant="body1">{selectedApplication.additionalInfo?.hasInsurance ? 'Yes' : 'No'}</Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />

              {/* Section 12: References */}
              <Typography variant="h6" gutterBottom>12. References - Applicant</Typography>
              {selectedApplication.applicantReferences?.map((ref, idx) => (
                <Grid container spacing={2} key={idx} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">Reference {idx + 1} Name</Typography>
                    <Typography variant="body1">{ref.name || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">Relationship</Typography>
                    <Typography variant="body1">{ref.relationship || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">Phone</Typography>
                    {ref.phone ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography 
                          component="a" 
                          href={`tel:${formatPhoneForDial(ref.phone)}`}
                          variant="body1"
                          sx={{ 
                            textDecoration: 'none', 
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          <PhoneIcon fontSize="small" />
                          {ref.phone}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setShowCallScript(showCallScript === 'personal' ? null : 'personal')}
                          sx={{ ml: 1 }}
                        >
                          View Script
                        </Button>
                      </Box>
                    ) : (
                      <Typography variant="body1">N/A</Typography>
                    )}
                  </Grid>
                  {showCallScript === 'personal' && idx === 0 && (
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Personal Reference Call Script
                        </Typography>
                        <Typography 
                          variant="body2" 
                          component="pre" 
                          sx={{ 
                            whiteSpace: 'pre-wrap', 
                            fontFamily: 'inherit',
                            fontSize: '0.875rem',
                            margin: 0,
                            mt: 1
                          }}
                        >
                          {getPopulatedCallScript('personal')}
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" gutterBottom>
                            Quick Note (Personal Reference Call)
                          </Typography>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            size="small"
                            placeholder="Add notes from this call..."
                            value={quickNote}
                            onChange={(e) => setQuickNote(e.target.value)}
                            sx={{ mb: 1, mt: 1 }}
                          />
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleAddNote(quickNote, 'reference_call')}
                            disabled={!quickNote.trim()}
                            startIcon={<NoteIcon />}
                          >
                            Save Note
                          </Button>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setShowCallScript(null)}
                          sx={{ mt: 1 }}
                        >
                          Close Script
                        </Button>
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              ))}
              <Divider sx={{ my: 2 }} />

              {/* Notes & Activity Tracking */}
              <Accordion expanded={expandedNotes} onChange={(e, isExpanded) => setExpandedNotes(isExpanded)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NoteIcon />
                    <Typography variant="h6">Notes & Activity Tracking</Typography>
                    {notes.length > 0 && (
                      <Chip label={notes.length} size="small" color="primary" />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Add Note"
                      placeholder="Record reference call notes, verification steps, or any other activity..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      sx={{ mb: 1 }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddNote}
                      disabled={!newNote.trim()}
                      startIcon={<NoteIcon />}
                    >
                      Add Note
                    </Button>
                  </Box>
                  
                  {notes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No notes yet. Add notes to track reference calls, verification steps, and other activities.
                    </Typography>
                  ) : (
                    <List>
                      {notes.map((note, idx) => (
                        <ListItem key={idx} sx={{ flexDirection: 'column', alignItems: 'flex-start', borderBottom: '1px solid', borderColor: 'divider' }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(note.timestamp).toLocaleString()}
                                </Typography>
                                {note.type && (
                                  <Chip label={note.type} size="small" />
                                )}
                              </Box>
                            }
                            secondary={
                              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                {note.note}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Section 14: Emergency Contact */}
              <Typography variant="h6" gutterBottom>14. Emergency Contact - Applicant</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Name</Typography>
                  <Typography variant="body1">{selectedApplication.applicantEmergencyContact?.name || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  {selectedApplication.applicantEmergencyContact?.phone ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                        component="a" 
                        href={`tel:${formatPhoneForDial(selectedApplication.applicantEmergencyContact?.phone)}`}
                        variant="body1"
                        sx={{ 
                          textDecoration: 'none', 
                          color: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        <PhoneIcon fontSize="small" />
                        {selectedApplication.applicantEmergencyContact?.phone}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body1">N/A</Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Relationship</Typography>
                  <Typography variant="body1">{selectedApplication.applicantEmergencyContact?.relationship || 'N/A'}</Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />

              {/* Submitted Date */}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Submitted Date</Typography>
                  <Typography variant="body1">{formatDate(selectedApplication.createdAt || selectedApplication.submittedAt)}</Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />

              {/* Credit Consent Signature Section */}
              <Typography variant="h6" gutterBottom>Credit Consent Signature</Typography>
              {(() => {
                const sigStatus = getSignatureStatus(selectedApplication);
                const sigData = signatureData.get(selectedApplication.id);
                
                if (selectedApplication.additionalInfo?.creditReportConsent !== true) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      Credit consent not required for this application.
                    </Typography>
                  );
                }

                if (!selectedApplication.creditConsentSignatureRequestId) {
                  return (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Credit consent was given but signature request was not created. Please contact support.
                    </Alert>
                  );
                }

                if (loadingSignatures && !sigData) {
                  return <CircularProgress size={24} />;
                }

                if (!sigData) {
                  return (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Unable to load signature request information.
                    </Alert>
                  );
                }

                return (
                  <Box>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Signature Status</Typography>
                        <Chip
                          label={getSignatureStatusLabel(sigStatus)}
                          color={getSignatureStatusColor(sigStatus)}
                          size="small"
                          icon={sigStatus === 'signed' ? <CheckCircleIcon /> : null}
                          sx={{ mt: 0.5 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Signature Request ID</Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {selectedApplication.creditConsentSignatureRequestId}
                        </Typography>
                      </Grid>
                      {sigStatus === 'pending' && selectedApplication.creditConsentSignatureRequestId && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>Signing Link</Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                              fullWidth
                              size="small"
                              value={`${window.location.origin}/sign/${selectedApplication.creditConsentSignatureRequestId}`}
                              InputProps={{
                                readOnly: true,
                                sx: { fontFamily: 'monospace', fontSize: '0.875rem' }
                              }}
                            />
                            <Tooltip title="Copy signing link">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const signingUrl = `${window.location.origin}/sign/${selectedApplication.creditConsentSignatureRequestId}`;
                                  navigator.clipboard.writeText(signingUrl);
                                  setSnackbar({
                                    open: true,
                                    message: 'Signing link copied to clipboard',
                                    severity: 'success'
                                  });
                                }}
                                aria-label="Copy signing link"
                              >
                                <ContentCopyIcon />
                              </IconButton>
                            </Tooltip>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                const signingUrl = `${window.location.origin}/sign/${selectedApplication.creditConsentSignatureRequestId}`;
                                window.open(signingUrl, '_blank');
                              }}
                            >
                              Open Link
                            </Button>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Send this link to the applicant if they need to sign the credit consent form
                          </Typography>
                        </Grid>
                      )}
                      {sigData.signers && sigData.signers.length > 0 && (
                        <>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Signer Name</Typography>
                            <Typography variant="body1">{sigData.signers[0].name || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Signer Email</Typography>
                            <Typography variant="body1">{sigData.signers[0].email || 'N/A'}</Typography>
                          </Grid>
                          {sigData.signers[0].signedAt && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">Signed Date</Typography>
                              <Typography variant="body1">
                                {new Date(sigData.signers[0].signedAt.seconds * 1000 || sigData.signers[0].signedAt).toLocaleString()}
                              </Typography>
                            </Grid>
                          )}
                        </>
                      )}
                      {sigStatus === 'signed' && sigData.pdfUrls?.signed && (
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<PdfIcon />}
                              onClick={() => window.open(sigData.pdfUrls.signed, '_blank')}
                            >
                              View Signed PDF
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<DownloadIcon />}
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = sigData.pdfUrls.signed;
                                link.download = `credit-consent-${selectedApplication.creditConsentSignatureRequestId}.pdf`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                            >
                              Download Signed PDF
                            </Button>
                          </Box>
                        </Grid>
                      )}
                      {sigStatus === 'pending' && (
                        <Grid item xs={12}>
                          <Alert severity="info" sx={{ mb: 1 }}>
                            Signature request is pending. The applicant will receive an email with a link to sign.
                          </Alert>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EmailIcon />}
                            onClick={() => handleResendSignatureEmail(selectedApplication)}
                          >
                            Resend Signature Email
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          {selectedApplication && selectedApplication.status === 'Pending' && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => handleStatusChange(
                  selectedApplication.id,
                  selectedApplication.propertyId,
                  selectedApplication.unitId,
                  'Approved'
                )}
                fullWidth={isMobile}
                size={isMobile ? 'medium' : 'medium'}
              >
                Approve
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => handleStatusChange(
                  selectedApplication.id,
                  selectedApplication.propertyId,
                  selectedApplication.unitId,
                  'Rejected'
                )}
                fullWidth={isMobile}
                size={isMobile ? 'medium' : 'medium'}
              >
                Reject
              </Button>
            </>
          )}
          <Button 
            onClick={() => setDetailDialogOpen(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* New Application Form Dialog */}
      <Dialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogContent sx={{ p: 0 }}>
          <RentalApplicationForm
            onClose={() => setFormDialogOpen(false)}
            onSuccess={(application) => {
              setFormDialogOpen(false);
              fetchApplications();
              setSnackbar({
                open: true,
                message: `Application submitted successfully! Reference: ${application.applicationReference}`,
                severity: 'success'
              });
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Generate Public Link Dialog */}
      <Dialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Generate Public Application Link
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Generate a public link that prospects can use to submit rental applications. You can optionally pre-select a specific property and unit.
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Property (Optional)</InputLabel>
            <Select
              value={selectedPropertyForLink}
              label="Property (Optional)"
              onChange={(e) => handlePropertyChangeForLink(e.target.value)}
            >
              <MenuItem value="">All your properties</MenuItem>
              {properties.map(property => (
                <MenuItem key={property.id} value={property.id}>
                  {property.name || property.address || `Property ${property.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedPropertyForLink && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Unit (Optional)</InputLabel>
              <Select
                value={selectedUnitForLink}
                label="Unit (Optional)"
                onChange={(e) => setSelectedUnitForLink(e.target.value)}
              >
                <MenuItem value="">All Units</MenuItem>
                {availableUnitsForLink.map(unit => (
                  <MenuItem key={unit.id} value={unit.id}>
                    {unit.fullLabel}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Public Application Link:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                fullWidth
                value={generatePublicLink() || 'Loading your link…'}
                InputProps={{
                  readOnly: true,
                }}
                size="small"
              />
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={copyLinkToClipboard}
                disabled={!generatePublicLink()}
              >
                Copy
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Share this link with prospects to allow them to submit rental applications.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setLinkDialogOpen(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request screening (simple flow when Compare link is off) */}
      <Dialog open={requestScreeningDialogOpen} onClose={() => setRequestScreeningDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request tenant screening</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a screening order using your default provider. Share the invite link with the applicant to pay and complete the check.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Country</InputLabel>
            <Select
              value={simpleScreeningCountry}
              label="Country"
              onChange={(e) => setSimpleScreeningCountry(e.target.value)}
            >
              {(screeningConfig.countries || ['CA', 'US']).map(c => (
                <MenuItem key={c} value={c}>{c === 'CA' ? 'Canada' : 'United States'}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Package</InputLabel>
            <Select
              value={simpleScreeningPackageId}
              label="Package"
              onChange={(e) => setSimpleScreeningPackageId(e.target.value)}
            >
              {(screeningConfig.packages || [
                { id: 'BASIC', label: 'Basic' },
                { id: 'STANDARD', label: 'Standard' },
                { id: 'PREMIUM', label: 'Premium' }
              ]).map(p => (
                <MenuItem key={p.id} value={p.id}>{p.label || p.id}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Paid by</InputLabel>
            <Select
              value={simpleScreeningPaidBy}
              label="Paid by"
              onChange={(e) => setSimpleScreeningPaidBy(e.target.value)}
            >
              <MenuItem value="applicant">Applicant</MenuItem>
              <MenuItem value="landlord">Landlord</MenuItem>
            </Select>
          </FormControl>
          {lastInviteUrl && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Invite link</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField size="small" fullWidth value={lastInviteUrl} InputProps={{ readOnly: true }} />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => { navigator.clipboard.writeText(lastInviteUrl); setSnackbar({ open: true, message: 'Link copied', severity: 'success' }); }}
                >
                  Copy
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestScreeningDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateSimpleScreeningOrder} disabled={simpleScreeningCreating}>
            {simpleScreeningCreating ? 'Creating…' : 'Create order'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={externalCompletionDialogOpen}
        onClose={() => !externalCompletionSubmitting && setExternalCompletionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Record external screening</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use this when the applicant was screened outside ZenRentFlow (e.g. another vendor or manual check). Notes are required for your audit trail.
          </Typography>
          <TextField
            label="Notes (required)"
            value={externalCompletionNotes}
            onChange={(e) => setExternalCompletionNotes(e.target.value)}
            fullWidth
            required
            multiline
            minRows={3}
            sx={{ mb: 2 }}
            placeholder="e.g. Completed via vendor portal on date; results stored in property file."
          />
          <TextField
            label="External provider name (optional)"
            value={externalCompletionProvider}
            onChange={(e) => setExternalCompletionProvider(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            placeholder="e.g. Certn, Checkr, local agency"
          />
          <TextField
            label="Report URL (optional)"
            value={externalCompletionReportUrl}
            onChange={(e) => setExternalCompletionReportUrl(e.target.value)}
            fullWidth
            placeholder="https://…"
            helperText="Must be http or https if provided."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExternalCompletionDialogOpen(false)} disabled={externalCompletionSubmitting}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitExternalCompletion} disabled={externalCompletionSubmitting}>
            {externalCompletionSubmitting ? 'Saving…' : 'Mark complete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={screeningActivityDialogOpen}
        onClose={() => setScreeningActivityDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Screening activity</DialogTitle>
        <DialogContent>
          {(() => {
            const orderId = screeningActivityDialogOrderId;
            const events = orderId ? (screeningOrderEventsById[orderId] || []) : [];
            const loading = orderId ? Boolean(loadingScreeningOrderEventsById[orderId]) : false;
            const loadingMore = orderId ? Boolean(loadingMoreScreeningOrderEventsById[orderId]) : false;
            const page = orderId ? (screeningOrderEventsPageById[orderId] || { hasMore: false }) : { hasMore: false };
            if (!orderId) return <Typography variant="body2" color="text.secondary">No order selected.</Typography>;
            if (loading) {
              return (
                <Box sx={{ py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">Loading activity…</Typography>
                </Box>
              );
            }
            if (events.length === 0) return <Typography variant="body2" color="text.secondary">No activity recorded yet.</Typography>;
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {events.map((event) => (
                  <Box key={event.id} sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="body2">{formatScreeningActivityEvent(event)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {event.createdAt ? new Date(event.createdAt).toLocaleString() : 'time unavailable'}
                    </Typography>
                  </Box>
                ))}
                {page.hasMore && (
                  <Box sx={{ pt: 0.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => loadMoreScreeningActivity(orderId)}
                      disabled={loadingMore}
                    >
                      {loadingMore ? 'Loading…' : 'Load more'}
                    </Button>
                  </Box>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScreeningActivityDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RentalApplicationManagement;






